import { Badge } from '@design/components/badge/Badge';
import { Chip } from '@design/components/chip/Chip';
import { Icon } from '@design/components/icon/Icon';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import clsx from 'clsx';
import { MouseEvent, ReactNode, useState } from 'react';

import styles from './MentionChip.module.scss';

export const MENTION_CHIP_KINDS = [
  'entity',
  'theme',
  'watchlist',
  'insight',
  'insight-scenario',
  'insight-catalyst-company',
  'insight-catalyst-market',
  'insight-earnings-preview',
  'insight-earnings-recap',
] as const;
export type MentionChipKind = (typeof MENTION_CHIP_KINDS)[number];

export interface MentionChipProps {
  kind: MentionChipKind;
  label: string;
  avatar?: ReactNode;
  readOnly?: boolean;
  isSelected?: boolean;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  'data-testid'?: string;
  showLabelTooltip?: boolean;
}

const entityInitialGlyph = (label: string): ReactNode => {
  const initial = label.trim().charAt(0).toUpperCase() || '?';

  // CSS `content` keeps the letter out of textContent / copy / flattened a11y text.
  return (
    <span
      className={styles.entityInitial}
      aria-hidden
      data-initial={initial}
      data-testid="mention-chip-entity-initial"
    />
  );
};

const defaultGlyph = (iconName: Parameters<typeof Icon>[0]['iconName']) => (
  <span className={styles.watchlistGlyphWrap}>
    <Icon iconName={iconName} size={8} className={styles.watchlistGlyph} />
  </span>
);

const kindGlyph = (kind: MentionChipKind, label: string): ReactNode => {
  switch (kind) {
    case 'entity':
      return entityInitialGlyph(label);
    case 'theme':
      // Same fallback as ThemeLogoIcon when no logo is available.
      return entityInitialGlyph(label);
    // Per design request, pasted-insight chips no longer carry a
    // category icon — just the colored pill and label.
    case 'insight':
    case 'insight-scenario':
    case 'insight-catalyst-company':
    case 'insight-catalyst-market':
    case 'insight-earnings-preview':
    case 'insight-earnings-recap':
      return undefined;
    case 'watchlist':
      return defaultGlyph('Watchlist');
  }
};

const kindClass: Record<MentionChipKind, string> = {
  entity: styles.entity,
  theme: styles.theme,
  watchlist: styles.watchlist,
  insight: styles.insight,
  'insight-scenario': styles.insightScenario,
  'insight-catalyst-company': styles.insightCatalystCompany,
  'insight-catalyst-market': styles.insightCatalystMarket,
  'insight-earnings-preview': styles.insightEarningsPreview,
  'insight-earnings-recap': styles.insightEarningsRecap,
};

export const MentionChip = ({
  kind,
  label,
  avatar,
  readOnly = false,
  isSelected = false,
  className,
  onClick,
  'data-testid': dataTestId,
  showLabelTooltip = true,
}: MentionChipProps) => {
  const [isLabelTruncated, setIsLabelTruncated] = useState(false);
  const hasTooltip = showLabelTooltip && isLabelTruncated;
  const glyph = avatar ?? kindGlyph(kind, label);
  const labelNode = (
    <span
      ref={el => setIsLabelTruncated(!!el && el.scrollWidth > el.clientWidth)}
      className={styles.label}
    >
      {label}
    </span>
  );

  const chip = readOnly ? (
    <Badge
      variant="textual"
      size="xsmall"
      label={labelNode}
      leftSection={glyph}
      className={clsx(
        styles.readOnly,
        kindClass[kind],
        { [styles.hoverable]: hasTooltip },
        className
      )}
      data-testid={dataTestId ?? 'mention-chip-readonly'}
    />
  ) : (
    <Chip
      label={labelNode}
      assetLogo={glyph}
      onClick={onClick}
      className={clsx(
        styles.interactive,
        kindClass[kind],
        { [styles.selected]: isSelected },
        className
      )}
      data-testid={dataTestId ?? 'mention-chip'}
    />
  );

  if (!showLabelTooltip) {
    return chip;
  }

  return (
    <Tooltip
      label={label}
      trigger={[TooltipTrigger.Hover, TooltipTrigger.Focus]}
      disabled={!isLabelTruncated}
      inPortal
      strategy="fixed"
    >
      {chip}
    </Tooltip>
  );
};
